import React, { createContext, ReactNode, useContext, useState } from 'react';
import flagsmith from 'flagsmith';
import { Session } from './AuthContext';
import { FeatureFlags } from '../flags';
import { SearchAgentResult } from '../system/search';
import { IFlags } from 'flagsmith/types';
import { useTenant } from './TenantContext';

interface FeatureFlagsContextType {
  hasFeature: (flag: string) => boolean;
  getFlags: () => IFlags | undefined;
  getFeatureValue: (flag: string, skipJSONParsing?: boolean) => any;
  setTrait: (trait: string, value: any) => Promise<void>;
  isLoading: boolean;
  error: Error | null;
  initializeFlags: (session: Session) => Promise<void>;
}

export const FeatureFlagsContext = createContext<
  FeatureFlagsContextType | undefined
>(undefined);

interface FeatureFlagsProviderProps {
  children: ReactNode;
}

const defaultFlags = {
  [FeatureFlags.NEXT_GENERATION_LISTING_MANAGEMENT]: false,
  [FeatureFlags.DEBUG]: true,
};

export const FeatureFlagsProvider: React.FC<FeatureFlagsProviderProps> = ({
  children,
}) => {
  const { getConfigValue } = useTenant();
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const createIdentity = (agent: SearchAgentResult) => ({
    member_key: agent.MemberKey,
    office_key: agent.OfficeKey,
    member_mls_id: agent.MemberMlsId,
    office_mls_id: agent.OfficeMlsId,
    member_email: agent.MemberEmail,
    broker_office_key: agent.OfficeBrokerKey,
    broker_office_mls_id: agent.OfficeBrokerMlsId,
    member_type: agent.MemberType,
    member_county: agent.MemberCounty,
  });

  const initializeFlags = async (session: Session) => {
    if (isInitialized) return;

    try {
      setIsLoading(true);
      const environmentKey = getConfigValue<string>('FLAGSMITH_API_KEY');

      if (!environmentKey) {
        console.warn('No Flagsmith API key, proceeding with default flags');
        setIsInitialized(true);
        setIsLoading(false);
        return;
      }

      await flagsmith.init({
        environmentID: environmentKey,
      });

      if (session?.user.agent) {
        const identity = createIdentity(session.user.agent);
        await flagsmith.identify(
          `member:${session.user.agent.MemberMlsId}`,
          identity,
        );
      }

      setIsInitialized(true);
    } catch (err) {
      console.error('Error initializing feature flags:', err);
      setError(
        err instanceof Error
          ? err
          : new Error('Failed to initialize feature flags'),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const hasFeature = (key: string): boolean => {
    if (!isInitialized) {
      return (defaultFlags as any)[key];
    }

    try {
      return flagsmith.hasFeature(key);
    } catch (err) {
      console.error(`Error checking feature flag ${key}:`, err);
      return (defaultFlags as any)[key];
    }
  };

  const setTrait = async (key: string, value: any) => {
    if (!isInitialized) {
      return;
    }

    try {
      await flagsmith.setTrait(key, value);
    } catch (err) {
      console.error(`Error setting trait feature flag ${key}:`, err);
    }
  };

  const getFlags = (): IFlags | undefined => {
    if (!isInitialized) {
      return undefined;
    }

    try {
      return flagsmith.getAllFlags();
    } catch {
      console.error(`Error getting flags `);
      return undefined;
    }
  };

  const getFeatureValue = (
    key: string,
    skipJSONParsing: boolean = false,
  ): any => {
    if (!isInitialized) {
      return undefined;
    }

    try {
      const value = flagsmith.getValue(key);
      if (skipJSONParsing) {
        return value;
      }

      if (typeof value === 'string') {
        return JSON.parse(value);
      }

      return value;
    } catch (err) {
      console.error(`Error getting feature flag ${key}:`, err);
      return undefined;
    }
  };

  return (
    <FeatureFlagsContext.Provider
      value={{
        hasFeature,
        setTrait,
        getFeatureValue,
        getFlags,
        isLoading,
        error,
        initializeFlags,
      }}
    >
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagsContext);
  if (context === undefined) {
    throw new Error(
      'useFeatureFlags must be used within a FeatureFlagsProvider',
    );
  }
  return context;
};
